'use client';

import { IconDocument } from '@repo/svgs';
import { cn } from '@/lib/cn';

interface DocumentPreviewProps {
  name: string;
  /** The file. Images get a thumbnail, and clicking opens it in a new tab. */
  url?: string;
  /** Preferred over the url extension when classifying the file. */
  mimeType?: string;
  /** Small line under the name, e.g. "Uploading…". */
  caption?: string;
  className?: string;
}

const IMAGE_URL = /\.(png|jpe?g|webp|heic|gif)(\?|$)/i;

/**
 * Thumbnail plus filename for a single document, opening the file on click.
 * Shared by the card that asks for an upload and the read-only card that shows
 * a stored one, so a file looks the same the moment it's picked and after it
 * comes back from the backend.
 */
export function DocumentPreview({ name, url, mimeType, caption, className }: DocumentPreviewProps) {
  const isImage = mimeType ? mimeType.startsWith('image/') : IMAGE_URL.test(url ?? '');

  const row = (
    <>
      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-secondary">
        {url && isImage ? (
          <img src={url} alt={name} className="h-full w-full object-cover" />
        ) : (
          <IconDocument className="h-8 w-8 icon-secondary" />
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-1 text-left">
        <p className="text-body-2 text-primary break-words">{name}</p>
        {caption ? <p className="text-caption text-secondary">{caption}</p> : null}
      </div>
    </>
  );

  if (!url) {
    return <div className={cn('flex items-center gap-4', className)}>{row}</div>;
  }

  // A plain link rather than a modal: the browser already renders images and
  // pdfs, and it keeps middle-click and "open in new tab" working.
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('flex items-center gap-4', className)}
    >
      {row}
    </a>
  );
}

export default DocumentPreview;
